import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { widgetsAPI, domainsAPI, Widget, Domain, DEFAULT_THEME } from '../lib/api';
import { useToast } from '../components/Toast';
import {
  ArrowLeft,
  Loader2,
  Settings,
  Globe,
  Code,
  BarChart3,
  Save,
  Plus,
  Copy,
  Check,
  Trash2,
  Eye,
  MessageSquare,
  Send,
  X,
} from 'lucide-react';

type TabType = 'settings' | 'domains' | 'embed' | 'analytics' | 'preview';

export default function WidgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'settings';
  const toast = useToast();

  const [widget, setWidget] = useState<Widget | null>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Form state for settings
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    welcome_message: '',
    placeholder_text: '',
    is_active: true,
    show_branding: true,
    theme_config: DEFAULT_THEME,
    rate_limit_messages: 20,
    rate_limit_sessions: 100,
  });

  // Embed code state
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedContainer, setCopiedContainer] = useState(false);
  const [copiedFullPage, setCopiedFullPage] = useState(false);

  // New domain state
  const [newDomain, setNewDomain] = useState('');
  const [isAddingDomain, setIsAddingDomain] = useState(false);

  useEffect(() => {
    if (id) {
      loadWidget();
    }
  }, [id]);

  const loadWidget = async () => {
    try {
      const [widgetData, domainsData, embedData] = await Promise.all([
        widgetsAPI.get(id!),
        domainsAPI.list(id!),
        widgetsAPI.getEmbedCode(id!),
      ]);

      setWidget(widgetData.widget);
      setDomains(domainsData.domains);
      setEmbedCode(embedData.embed_code);

      // Merge with defaults to ensure new fields like embedMode exist
      const mergedThemeConfig = {
        ...DEFAULT_THEME,
        ...widgetData.widget.theme_config,
      };

      setFormData({
        name: widgetData.widget.name,
        description: widgetData.widget.description || '',
        welcome_message: widgetData.widget.welcome_message,
        placeholder_text: widgetData.widget.placeholder_text,
        is_active: widgetData.widget.is_active,
        show_branding: widgetData.widget.show_branding,
        theme_config: mergedThemeConfig,
        rate_limit_messages: widgetData.widget.rate_limit_messages || 20,
        rate_limit_sessions: widgetData.widget.rate_limit_sessions || 100,
      });
    } catch (error) {
      console.error('Failed to load widget:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await widgetsAPI.update(id!, formData);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;

    setIsAddingDomain(true);
    try {
      const result = await domainsAPI.add(id!, newDomain.trim());
      setDomains([...domains, result.domain]);
      setNewDomain('');
      toast.success(`Domain ${newDomain.trim()} added`);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to add domain');
    } finally {
      setIsAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domainId: string) => {
    if (!confirm('Remove this domain?')) return;

    try {
      await domainsAPI.remove(id!, domainId);
      setDomains(domains.filter((d) => d.id !== domainId));
      toast.success('Domain removed');
    } catch (error) {
      toast.error('Failed to remove domain');
    }
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Embed code copied to clipboard');
  };

  const getInlineContainerCode = () => {
    const width = formData.theme_config.inlineWidth || '100%';
    const height = formData.theme_config.inlineHeight || '600px';
    return `<div data-chatbot-embed="${id}" style="width: ${width}; height: ${height};"></div>`;
  };

  const copyInlineContainerCode = () => {
    navigator.clipboard.writeText(getInlineContainerCode());
    setCopiedContainer(true);
    setTimeout(() => setCopiedContainer(false), 2000);
  };

  const getFullPageCode = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <title>Chat</title>
  <style>
    body { margin: 0; padding: 20px; }
    .chat-container {
      max-width: 800px;
      margin: 0 auto;
    }
  </style>
</head>
<body>
  <div class="chat-container">
    ${getInlineContainerCode()}
  </div>
  ${embedCode}
</body>
</html>`;
  };

  const copyFullPageCode = () => {
    navigator.clipboard.writeText(getFullPageCode());
    setCopiedFullPage(true);
    setTimeout(() => setCopiedFullPage(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!widget) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Widget not found</p>
      </div>
    );
  }

  const tabs = [
    { id: 'settings' as const, name: 'Settings', icon: Settings },
    { id: 'preview' as const, name: 'Preview', icon: Eye },
    { id: 'domains' as const, name: 'Domains', icon: Globe },
    { id: 'embed' as const, name: 'Embed Code', icon: Code },
    { id: 'analytics' as const, name: 'Analytics', icon: BarChart3 },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/widgets')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Widgets
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: widget.theme_config.primaryColor }}
            >
              <span className="text-2xl text-white">💬</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{widget.name}</h1>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  widget.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {widget.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Basic Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Basic Settings
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Widget Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Widget Active</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_branding}
                    onChange={(e) =>
                      setFormData({ ...formData, show_branding: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Show Branding</span>
                </label>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Messages</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Welcome Message
                </label>
                <input
                  type="text"
                  value={formData.welcome_message}
                  onChange={(e) =>
                    setFormData({ ...formData, welcome_message: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Input Placeholder
                </label>
                <input
                  type="text"
                  value={formData.placeholder_text}
                  onChange={(e) =>
                    setFormData({ ...formData, placeholder_text: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Embed Mode */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Embed Mode
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Choose how the widget appears on your website
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                onClick={() =>
                  setFormData({
                    ...formData,
                    theme_config: { ...formData.theme_config, embedMode: 'popup' },
                  })
                }
                className={`relative flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.theme_config.embedMode === 'popup'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900">Popup Widget</span>
                </div>
                <p className="text-sm text-gray-600">
                  Floating button in the corner that opens a chat window.
                </p>
              </div>

              <div
                onClick={() =>
                  setFormData({
                    ...formData,
                    theme_config: { ...formData.theme_config, embedMode: 'inline' },
                  })
                }
                className={`relative flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.theme_config.embedMode === 'inline'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900">Inline Embed</span>
                </div>
                <p className="text-sm text-gray-600">
                  Full-size chat embedded directly in your page.
                </p>
              </div>
            </div>

            {/* Inline Size Options */}
            {formData.theme_config.embedMode === 'inline' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Inline Embed Size</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Width</label>
                    <input
                      type="text"
                      value={formData.theme_config.inlineWidth}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          theme_config: { ...formData.theme_config, inlineWidth: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      placeholder="100%"
                    />
                    <p className="text-xs text-gray-400 mt-1">e.g., 100%, 800px, 50vw</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Height</label>
                    <input
                      type="text"
                      value={formData.theme_config.inlineHeight}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          theme_config: { ...formData.theme_config, inlineHeight: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      placeholder="600px"
                    />
                    <p className="text-xs text-gray-400 mt-1">e.g., 600px, 100vh, 80%</p>
                  </div>
                </div>
              </div>
            )}

            {/* Popup Position Options */}
            {formData.theme_config.embedMode === 'popup' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Button Position</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Corner</label>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Top Left */}
                      <div
                        onClick={() =>
                          setFormData({
                            ...formData,
                            theme_config: { ...formData.theme_config, buttonPosition: 'top-left' },
                          })
                        }
                        className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                          formData.theme_config.buttonPosition === 'top-left'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                          <div className={`absolute top-0.5 left-0.5 w-2 h-2 rounded-full ${formData.theme_config.buttonPosition === 'top-left' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                        </div>
                        <span className={`text-sm ${formData.theme_config.buttonPosition === 'top-left' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Top Left</span>
                      </div>
                      {/* Top Right */}
                      <div
                        onClick={() =>
                          setFormData({
                            ...formData,
                            theme_config: { ...formData.theme_config, buttonPosition: 'top-right' },
                          })
                        }
                        className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                          formData.theme_config.buttonPosition === 'top-right'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                          <div className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full ${formData.theme_config.buttonPosition === 'top-right' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                        </div>
                        <span className={`text-sm ${formData.theme_config.buttonPosition === 'top-right' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Top Right</span>
                      </div>
                      {/* Bottom Left */}
                      <div
                        onClick={() =>
                          setFormData({
                            ...formData,
                            theme_config: { ...formData.theme_config, buttonPosition: 'bottom-left' },
                          })
                        }
                        className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                          formData.theme_config.buttonPosition === 'bottom-left'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                          <div className={`absolute bottom-0.5 left-0.5 w-2 h-2 rounded-full ${formData.theme_config.buttonPosition === 'bottom-left' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                        </div>
                        <span className={`text-sm ${formData.theme_config.buttonPosition === 'bottom-left' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Bottom Left</span>
                      </div>
                      {/* Bottom Right */}
                      <div
                        onClick={() =>
                          setFormData({
                            ...formData,
                            theme_config: { ...formData.theme_config, buttonPosition: 'bottom-right' },
                          })
                        }
                        className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                          formData.theme_config.buttonPosition === 'bottom-right'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                          <div className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full ${formData.theme_config.buttonPosition === 'bottom-right' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                        </div>
                        <span className={`text-sm ${formData.theme_config.buttonPosition === 'bottom-right' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Bottom Right</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Horizontal Offset (px)</label>
                      <input
                        type="number"
                        value={formData.theme_config.buttonOffsetX}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            theme_config: { ...formData.theme_config, buttonOffsetX: parseInt(e.target.value) || 0 },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Vertical Offset (px)</label>
                      <input
                        type="number"
                        value={formData.theme_config.buttonOffsetY}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            theme_config: { ...formData.theme_config, buttonOffsetY: parseInt(e.target.value) || 0 },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Theme */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Theme Colors
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'primaryColor', label: 'Primary' },
                { key: 'headerBackgroundColor', label: 'Header' },
                { key: 'buttonColor', label: 'Button' },
                { key: 'userBubbleColor', label: 'User Bubble' },
                { key: 'assistantBubbleColor', label: 'Assistant Bubble' },
                { key: 'backgroundColor', label: 'Background' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={
                        formData.theme_config[key as keyof typeof formData.theme_config] as string
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          theme_config: {
                            ...formData.theme_config,
                            [key]: e.target.value,
                          },
                        })
                      }
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={
                        formData.theme_config[key as keyof typeof formData.theme_config] as string
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          theme_config: {
                            ...formData.theme_config,
                            [key]: e.target.value,
                          },
                        })
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Limits */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Rate Limits
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Protect your widget from abuse by limiting how many messages users can send.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Messages per Session (per hour)
                </label>
                <input
                  type="number"
                  value={formData.rate_limit_messages}
                  onChange={(e) =>
                    setFormData({ ...formData, rate_limit_messages: parseInt(e.target.value) || 20 })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  min="1"
                  max="1000"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum messages a single user can send per hour
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sessions per Domain (per hour)
                </label>
                <input
                  type="number"
                  value={formData.rate_limit_sessions}
                  onChange={(e) =>
                    setFormData({ ...formData, rate_limit_sessions: parseInt(e.target.value) || 100 })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  min="1"
                  max="10000"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum new chat sessions per domain per hour
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Tip:</strong> Start with conservative limits and increase as needed. Lower limits protect against abuse but may affect legitimate users.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-green-600">{saveMessage}</span>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      )}

      {activeTab === 'domains' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Allowed Domains
            </h2>
            <p className="text-sm text-gray-600">
              The widget will only load on these domains. Use *.example.com for
              wildcards.
            </p>
          </div>

          {/* Add Domain */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="example.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
              />
              <button
                onClick={handleAddDomain}
                disabled={isAddingDomain || !newDomain.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {isAddingDomain ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add
              </button>
            </div>
          </div>

          {/* Domains List */}
          <div className="divide-y divide-gray-200">
            {domains.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No domains configured
              </div>
            ) : (
              domains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between px-6 py-4"
                >
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-400" />
                    <span className="font-mono text-sm">{domain.domain}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveDomain(domain.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove domain"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'embed' && (
        <div className="space-y-6">
          {formData.theme_config.embedMode === 'inline' ? (
            <>
              {/* Inline Mode - Step 1: Container Code */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold">1</span>
                  <h2 className="text-lg font-semibold text-gray-900">Container Element</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Add this container element where you want the chat to appear on your page.
                </p>

                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                    <code>{getInlineContainerCode()}</code>
                  </pre>
                  <button
                    onClick={copyInlineContainerCode}
                    className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    {copiedContainer ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-3">
                  The widget will use the dimensions configured in Settings: {formData.theme_config.inlineWidth} x {formData.theme_config.inlineHeight}
                </p>
              </div>

              {/* Inline Mode - Step 2: Script Code */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold">2</span>
                  <h2 className="text-lg font-semibold text-gray-900">Embed Script</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Add this script after the container element or before the closing &lt;/body&gt; tag.
                </p>

                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                    <code>{embedCode}</code>
                  </pre>
                  <button
                    onClick={copyEmbedCode}
                    className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Quick Start for Inline */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h3 className="font-medium text-purple-900 mb-3">Quick Start Checklist</h3>
                <ol className="text-sm text-purple-800 space-y-2 list-decimal list-inside">
                  <li>Copy the container element (Step 1) and place it where you want the chat</li>
                  <li>Copy the embed script (Step 2) and add it after the container</li>
                  <li>Make sure your domain is in the allowed domains list</li>
                  <li>Refresh your page to see the inline chat widget</li>
                </ol>
              </div>

              {/* Full Page Example for Inline */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-medium text-gray-900 mb-3">Full Page Example</h3>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                    <code>{getFullPageCode()}</code>
                  </pre>
                  <button
                    onClick={copyFullPageCode}
                    className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    {copiedFullPage ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Popup Mode */
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Embed Code
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Copy this code and paste it into your website's HTML, just before the closing &lt;/body&gt; tag.
              </p>

              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                  <code>{embedCode}</code>
                </pre>
                <button
                  onClick={copyEmbedCode}
                  className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Quick Start</h3>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Copy the embed code above</li>
                  <li>Paste it into your HTML file, just before the closing &lt;/body&gt; tag</li>
                  <li>Make sure your domain is in the allowed domains list</li>
                  <li>Refresh your page to see the floating chat button</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Widget Analytics
          </h2>
          <p className="text-gray-600 mb-4">
            View detailed analytics for this widget
          </p>
          <button
            onClick={() => navigate(`/analytics/${id}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            View Analytics
          </button>
        </div>
      )}

      {activeTab === 'preview' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Widget Preview
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              This is how your widget will look to visitors. Changes from Settings are reflected here in real-time.
            </p>

            {/* Preview Container */}
            <div className="flex justify-center">
              {formData.theme_config.embedMode === 'popup' ? (
                /* Popup Preview */
                <div className="relative" style={{ width: '420px', height: '650px' }}>
                  {/* Mock page background */}
                  <div className="absolute inset-0 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <span className="text-gray-400 text-sm">Your website</span>
                  </div>

                  {/* Chat Window */}
                  <div
                    className="absolute flex flex-col overflow-hidden shadow-2xl"
                    style={{
                      width: `${Math.min(formData.theme_config.windowWidth || 380, 380)}px`,
                      height: `${Math.min(formData.theme_config.windowHeight || 500, 500)}px`,
                      backgroundColor: formData.theme_config.backgroundColor,
                      borderRadius: `${formData.theme_config.borderRadius}px`,
                      ...(formData.theme_config.buttonPosition?.includes('bottom') ? { bottom: '70px' } : { top: '70px' }),
                      ...(formData.theme_config.buttonPosition?.includes('right') ? { right: '10px' } : { left: '10px' }),
                    }}
                  >
                    {/* Header */}
                    <div
                      className="flex items-center gap-3 p-4 flex-shrink-0"
                      style={{
                        backgroundColor: formData.theme_config.headerBackgroundColor || formData.theme_config.primaryColor,
                        color: formData.theme_config.headerTextColor,
                      }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                      >
                        <MessageSquare className="w-5 h-5" style={{ color: formData.theme_config.headerTextColor }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{formData.name || 'Chat Assistant'}</h3>
                        <p className="text-xs opacity-80">Ask me anything</p>
                      </div>
                      <button className="opacity-80 hover:opacity-100">
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ backgroundColor: formData.theme_config.backgroundColor }}>
                      {/* Assistant Message */}
                      <div
                        className="max-w-[80%] p-3 rounded-2xl rounded-bl-sm"
                        style={{
                          backgroundColor: formData.theme_config.assistantBubbleColor,
                          color: formData.theme_config.assistantBubbleTextColor,
                          fontSize: `${formData.theme_config.fontSize}px`,
                          fontFamily: formData.theme_config.fontFamily,
                        }}
                      >
                        {formData.welcome_message || 'Hello! How can I help you today?'}
                      </div>

                      {/* User Message */}
                      <div
                        className="max-w-[80%] p-3 rounded-2xl rounded-br-sm ml-auto"
                        style={{
                          backgroundColor: formData.theme_config.userBubbleColor || formData.theme_config.primaryColor,
                          color: formData.theme_config.userBubbleTextColor,
                          fontSize: `${formData.theme_config.fontSize}px`,
                          fontFamily: formData.theme_config.fontFamily,
                        }}
                      >
                        Hi, I have a question!
                      </div>

                      {/* Assistant Response */}
                      <div
                        className="max-w-[80%] p-3 rounded-2xl rounded-bl-sm"
                        style={{
                          backgroundColor: formData.theme_config.assistantBubbleColor,
                          color: formData.theme_config.assistantBubbleTextColor,
                          fontSize: `${formData.theme_config.fontSize}px`,
                          fontFamily: formData.theme_config.fontFamily,
                        }}
                      >
                        Of course! I'd be happy to help. What would you like to know?
                      </div>
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t flex gap-2 flex-shrink-0" style={{ backgroundColor: formData.theme_config.backgroundColor, borderColor: '#e5e7eb' }}>
                      <input
                        type="text"
                        placeholder={formData.placeholder_text || 'Type your message...'}
                        className="flex-1 px-4 py-2 border rounded-full text-sm outline-none"
                        style={{
                          fontSize: `${formData.theme_config.fontSize}px`,
                          fontFamily: formData.theme_config.fontFamily,
                        }}
                        readOnly
                      />
                      <button
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: formData.theme_config.primaryColor }}
                      >
                        <Send className="w-4 h-4 text-white" />
                      </button>
                    </div>

                    {/* Footer */}
                    {formData.show_branding && (
                      <div className="text-center py-2 text-xs text-gray-400" style={{ backgroundColor: formData.theme_config.backgroundColor }}>
                        Powered by <span className="text-gray-500">ChatbotEmbed</span>
                      </div>
                    )}
                  </div>

                  {/* Floating Button */}
                  <div
                    className="absolute w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-pointer"
                    style={{
                      backgroundColor: formData.theme_config.buttonColor || formData.theme_config.primaryColor,
                      ...(formData.theme_config.buttonPosition?.includes('bottom') ? { bottom: '10px' } : { top: '10px' }),
                      ...(formData.theme_config.buttonPosition?.includes('right') ? { right: '10px' } : { left: '10px' }),
                    }}
                  >
                    <MessageSquare className="w-6 h-6" style={{ color: formData.theme_config.buttonIconColor }} />
                  </div>
                </div>
              ) : (
                /* Inline Preview */
                <div
                  className="flex flex-col overflow-hidden shadow-lg border"
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    height: '500px',
                    backgroundColor: formData.theme_config.backgroundColor,
                    borderRadius: `${formData.theme_config.borderRadius}px`,
                    borderColor: '#e5e7eb',
                  }}
                >
                  {/* Header */}
                  <div
                    className="flex items-center gap-3 p-4 flex-shrink-0"
                    style={{
                      backgroundColor: formData.theme_config.headerBackgroundColor || formData.theme_config.primaryColor,
                      color: formData.theme_config.headerTextColor,
                    }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                    >
                      <MessageSquare className="w-5 h-5" style={{ color: formData.theme_config.headerTextColor }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm">{formData.name || 'Chat Assistant'}</h3>
                      <p className="text-xs opacity-80">Ask me anything</p>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ backgroundColor: formData.theme_config.backgroundColor }}>
                    {/* Assistant Message */}
                    <div
                      className="max-w-[70%] p-3 rounded-2xl rounded-bl-sm"
                      style={{
                        backgroundColor: formData.theme_config.assistantBubbleColor,
                        color: formData.theme_config.assistantBubbleTextColor,
                        fontSize: `${formData.theme_config.fontSize}px`,
                        fontFamily: formData.theme_config.fontFamily,
                      }}
                    >
                      {formData.welcome_message || 'Hello! How can I help you today?'}
                    </div>

                    {/* User Message */}
                    <div
                      className="max-w-[70%] p-3 rounded-2xl rounded-br-sm ml-auto"
                      style={{
                        backgroundColor: formData.theme_config.userBubbleColor || formData.theme_config.primaryColor,
                        color: formData.theme_config.userBubbleTextColor,
                        fontSize: `${formData.theme_config.fontSize}px`,
                        fontFamily: formData.theme_config.fontFamily,
                      }}
                    >
                      Hi, I have a question!
                    </div>

                    {/* Assistant Response */}
                    <div
                      className="max-w-[70%] p-3 rounded-2xl rounded-bl-sm"
                      style={{
                        backgroundColor: formData.theme_config.assistantBubbleColor,
                        color: formData.theme_config.assistantBubbleTextColor,
                        fontSize: `${formData.theme_config.fontSize}px`,
                        fontFamily: formData.theme_config.fontFamily,
                      }}
                    >
                      Of course! I'd be happy to help. What would you like to know?
                    </div>
                  </div>

                  {/* Input */}
                  <div className="p-3 border-t flex gap-2 flex-shrink-0" style={{ backgroundColor: formData.theme_config.backgroundColor, borderColor: '#e5e7eb' }}>
                    <input
                      type="text"
                      placeholder={formData.placeholder_text || 'Type your message...'}
                      className="flex-1 px-4 py-2 border rounded-full text-sm outline-none"
                      style={{
                        fontSize: `${formData.theme_config.fontSize}px`,
                        fontFamily: formData.theme_config.fontFamily,
                      }}
                      readOnly
                    />
                    <button
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: formData.theme_config.primaryColor }}
                    >
                      <Send className="w-4 h-4 text-white" />
                    </button>
                  </div>

                  {/* Footer */}
                  {formData.show_branding && (
                    <div className="text-center py-2 text-xs text-gray-400" style={{ backgroundColor: formData.theme_config.backgroundColor }}>
                      Powered by <span className="text-gray-500">ChatbotEmbed</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Preview Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-medium text-blue-900 mb-2">Preview Tips</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Go to <strong>Settings</strong> tab to customize colors, messages, and position</li>
              <li>Changes are reflected here instantly - no need to save first</li>
              <li>The actual widget on your site will match this preview</li>
              <li>Toggle <strong>Show Branding</strong> in Settings to show/hide the footer</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
